import * as React from "react";

import { cn } from "../../../lib/utils";
import { SplitButtonContext } from "./split-button-context";
import type {
	SplitButtonLayoutProps,
	SplitButtonSize,
	SplitButtonVariant,
} from "./split-button-types";

/**
 * SplitButtonLayout renders a split button group consisting of a leading
 * action button and a trailing menu toggle button.
 *
 * @example
 * <SplitButtonLayout
 *   leadingButton={<SplitButtonLeading onClick={handleSave}>Save</SplitButtonLeading>}
 *   trailingButton={
 *     <SplitButtonTrailing
 *       checked={open}
 *       onCheckedChange={setOpen}
 *       aria-label="More save options"
 *     />
 *   }
 * />
 */
const SplitButtonLayoutComponent = React.forwardRef<
	HTMLDivElement,
	SplitButtonLayoutProps
>(
	(
		{
			leadingButton,
			trailingButton,
			className,
			spacing = 2,
			variant: layoutVariant,
			size: layoutSize,
			...restProps
		},
		ref,
	) => {
		// Extract size and variant from the leading button props
		const leadingProps = React.isValidElement<{
			size?: SplitButtonSize;
			variant?: SplitButtonVariant;
		}>(leadingButton)
			? leadingButton.props
			: {};

		const size = layoutSize || leadingProps.size;
		const variant = layoutVariant || leadingProps.variant;

		const contextValue = React.useMemo(
			() => ({
				size,
				variant,
			}),
			[size, variant],
		);

		return (
			<SplitButtonContext.Provider value={contextValue}>
				{/* biome-ignore lint/a11y/useSemanticElements: Split button group needs div with role="group", not fieldset */}
				<div
					ref={ref}
					role="group"
					className={cn(
						"inline-flex flex-row items-stretch justify-center w-fit shrink-0 relative",
						className,
					)}
					style={{ gap: `${spacing}px`, ...restProps.style }}
					{...restProps}
				>
					{leadingButton}
					{trailingButton}
				</div>
			</SplitButtonContext.Provider>
		);
	},
);

SplitButtonLayoutComponent.displayName = "SplitButtonLayout";

export const SplitButtonLayout = React.memo(SplitButtonLayoutComponent);
